import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DownloadSimple, MagnifyingGlass, Lightning, Clock, X } from "@phosphor-icons/react";

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const UPD_RE = /\s*\{updated:\s*(\d{4}-\d{2}-\d{2})\}\s*$/;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDate = (d) => { const p = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d || ""); return p ? `${MONTHS[+p[2] - 1]} ${+p[3]}, ${p[1]}` : ""; };

const GUIDE_ASSET_BASE = process.env.REACT_APP_BACKEND_URL || "";
const resolveAsset = (u) => (u && u.startsWith("/api") ? GUIDE_ASSET_BASE + u : u);
const escAttr = (s) => (s || "").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const escHtml = (s) => (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Wrap matches of `q` in <mark>, but only inside text (never inside HTML tags).
const highlight = (html, q) => {
  if (!q) return html;
  const re = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
  return html.split(/(<[^>]+>)/).map((seg) => (seg.startsWith("<") ? seg : seg.replace(re, "<mark>$&</mark>"))).join("");
};

// XSS-safe Markdown -> HTML (escape first, then whitelisted tags).
const mdToHtml = (md) => {
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) => esc(s)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`(.+?)`/g, "<code>$1</code>")
    .replace(/\[(.+?)\]\((#.+?)\)/g, '<a class="cg-link" href="$2">$1</a>');
  const lines = (md || "").split("\n");
  let html = "", listType = null, inTable = false;
  const closeList = () => { if (listType) { html += listType === "ol" ? "</ol>" : "</ul>"; listType = null; } };
  const openList = (type) => { if (listType !== type) { closeList(); html += type === "ol" ? "<ol>" : "<ul>"; listType = type; } };
  const closeTable = () => { if (inTable) { html += "</tbody></table>"; inTable = false; } };
  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (line.startsWith("|")) {
      const cells = line.split("|").slice(1, -1).map((c) => c.trim());
      if (cells.every((c) => /^-+$/.test(c) || c === "")) continue;
      if (!inTable) { closeList(); html += '<table class="cg-table"><tbody>'; inTable = true; }
      html += "<tr>" + cells.map((c) => `<td>${inline(c)}</td>`).join("") + "</tr>";
      continue;
    }
    closeTable();
    if (line.startsWith("### ")) { closeList(); html += `<h3>${inline(line.slice(4))}</h3>`; }
    else if (line.startsWith("## ")) { closeList(); html += `<h2>${inline(line.slice(3).replace(UPD_RE, ""))}</h2>`; }
    else if (line.startsWith("# ")) { closeList(); html += `<h1>${inline(line.slice(2))}</h1>`; }
    else if (line.startsWith("> ")) { closeList(); html += `<blockquote>${inline(line.slice(2))}</blockquote>`; }
    else if (/^!\[[^\]]*\]\([^)]+\)\s*$/.test(line)) { closeList(); const mm = line.match(/^!\[([^\]]*)\]\(([^)]+)\)/); html += `<img class="cg-img" src="${escAttr(resolveAsset(mm[2]))}" alt="${escAttr(mm[1])}" loading="lazy"/>`; }
    else if (/^\s*\d+\.\s+/.test(line)) { openList("ol"); html += `<li>${inline(line.replace(/^\s*\d+\.\s+/, ""))}</li>`; }
    else if (/^\s*-\s+/.test(line)) { openList("ul"); html += `<li>${inline(line.replace(/^\s*-\s+/, ""))}</li>`; }
    else if (line.trim() === "---") { closeList(); html += "<hr/>"; }
    else if (!line.trim()) { closeList(); }
    else { closeList(); html += `<p>${inline(line)}</p>`; }
  }
  closeList(); closeTable();
  return html;
};

// Split markdown into an intro block + ## sections (each with slug + searchable text).
const parseSections = (md) => {
  const lines = (md || "").split("\n");
  const intro = [];
  const sections = [];
  let cur = null;
  for (const line of lines) {
    if (line.startsWith("## ")) {
      if (cur) sections.push(cur);
      const rawTitle = line.slice(3).trim();
      const um = rawTitle.match(UPD_RE);
      const title = rawTitle.replace(UPD_RE, "").trim();
      cur = { title, slug: slugify(title), updated: um ? um[1] : null, body: [] };
    } else if (cur) cur.body.push(line);
    else intro.push(line);
  }
  if (cur) sections.push(cur);
  return {
    introMd: intro.join("\n"),
    sections: sections.map((s) => ({ ...s, bodyMd: s.body.join("\n"), text: (s.title + " " + s.body.join(" ")).toLowerCase() })),
  };
};

export default function UserGuide({ superadmin = false, resourceKey = null }) {
  const loc = useLocation();
  const nav = useNavigate();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const refs = useRef({});

  useEffect(() => {
    (async () => {
      try {
        const url = resourceKey ? `/guides/resource/${resourceKey}` : (superadmin ? "/guides/superadmin" : "/guides/me");
        const { data } = await api.get(url);
        setGuide(data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load the guide."); }
      setLoading(false);
    })();
  }, [superadmin, resourceKey]);

  const parsed = useMemo(() => (guide ? parseSections(guide.markdown) : { introMd: "", sections: [] }), [guide]);

  // Contextual deep-link: /guide?q=<keyword> pre-fills search + scrolls to first match.
  useEffect(() => {
    if (!guide) return;
    const params = new URLSearchParams(loc.search);
    const focus = params.get("q");
    if (focus) {
      setQ(focus);
      const hit = parsed.sections.find((s) => s.text.includes(focus.toLowerCase()));
      if (hit) setTimeout(() => refs.current[hit.slug]?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [guide, loc.search, parsed.sections]);

  const download = async () => {
    try {
      const res = await api.get(`/guides/${guide.key}/pdf`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const name = m ? m[1] : `${(guide.title || "User_Manual").split("—")[0].trim().replace(/[^A-Za-z0-9]+/g, "_")}.pdf`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed."); }
  };

  const jumpTo = (slug) => refs.current[slug]?.scrollIntoView({ behavior: "smooth", block: "start" });

  const toggleCheck = async (m, next) => {
    setGuide((g) => ({ ...g, meta: { ...g.meta, modules: g.meta.modules.map((x) => x.key === m.key ? { ...x, done: next } : x) } }));
    try { await api.post("/guides/checklist", { module_key: m.key, done: next }); }
    catch (e) { toast.error("Could not save progress."); setGuide((g) => ({ ...g, meta: { ...g.meta, modules: g.meta.modules.map((x) => x.key === m.key ? { ...x, done: !next } : x) } })); }
  };

  if (loading) return <p className="text-sm text-muted-foreground p-6">Loading…</p>;
  if (!guide) return <p className="text-sm text-muted-foreground p-6">Guide unavailable.</p>;

  const ql = q.trim().toLowerCase();
  const visible = ql ? parsed.sections.filter((s) => s.text.includes(ql)) : parsed.sections;
  const meta = guide.meta || {};

  return (
    <div data-testid="user-guide-page" className="pb-16">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold" data-testid="guide-title">{guide.title}</h1>
          <p className="text-sm text-muted-foreground">{resourceKey ? "A step-by-step guide to setting up and testing your own encryption key." : superadmin ? "Internal operator how-to — Claro Super Admins only." : "Your ClaroSuite user manual, tailored to your plan."}</p>
        </div>
        <div className="flex items-center gap-2">
          {meta.is_trial && (
            <div data-testid="guide-trial-badge" className="flex items-center gap-2 border border-amber-300 bg-amber-50 text-amber-900 px-3 py-1.5 text-sm rounded-none">
              <Clock size={16} weight="fill" />
              <span>{meta.trial_days_left != null ? <><b>{meta.trial_days_left}</b> day{meta.trial_days_left === 1 ? "" : "s"} left in your trial</> : "Free trial"}</span>
              <Button size="sm" className="rounded-none h-7 bg-amber-600 hover:bg-amber-700 ml-1" data-testid="guide-upgrade-btn" onClick={() => nav("/billing")}>
                <Lightning size={13} className="mr-1" />Upgrade
              </Button>
            </div>
          )}
          <Button className="rounded-none" data-testid="guide-download-btn" onClick={download} disabled={meta.is_trial}
            title={meta.is_trial ? "Upgrade to download the guide" : "Download PDF"}>
            <DownloadSimple size={16} className="mr-1" />Download PDF
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md mb-4">
        <MagnifyingGlass size={15} className="absolute left-2.5 top-2.5 text-muted-foreground" />
        <Input data-testid="guide-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search this guide…" className="rounded-none pl-8 pr-8" />
        {q && <button data-testid="guide-search-clear" onClick={() => setQ("")} className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"><X size={15} /></button>}
      </div>
      {ql && <p className="text-xs text-muted-foreground mb-3" data-testid="guide-search-count">{visible.length} section{visible.length === 1 ? "" : "s"} match "{q}"</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 max-w-5xl">
        {/* Left rail: tailored module list + section jump */}
        <aside className="hidden lg:block text-sm">
          {!superadmin && meta.modules && meta.modules.length > 0 && (
            <div className="mb-5" data-testid="guide-my-modules">
              {(() => {
                const doneN = meta.modules.filter((m) => m.done).length;
                const totalN = meta.modules.length;
                const pct = Math.round((doneN / totalN) * 100);
                return (
                  <>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Setup checklist</div>
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1" data-testid="guide-checklist-progress">
                      <span>{doneN} of {totalN} explored</span><span>{pct}%</span>
                    </div>
                    <div className="h-1.5 bg-secondary w-full mb-2"><div className="h-1.5 bg-emerald-600 transition-all" style={{ width: `${pct}%` }} /></div>
                    <div className="flex flex-col gap-1">
                      {meta.modules.map((m) => (
                        <label key={m.key} data-testid={`guide-check-${m.key}`} className="flex items-start gap-1.5 text-xs cursor-pointer group">
                          <input type="checkbox" checked={!!m.done} onChange={(e) => toggleCheck(m, e.target.checked)}
                            data-testid={`guide-check-input-${m.key}`} className="mt-0.5 accent-emerald-600 cursor-pointer" />
                          <button type="button" onClick={() => setQ(m.label)}
                            className={`text-left truncate group-hover:underline ${m.done ? "text-muted-foreground line-through" : "text-[#071B3E]"}`}>{m.label}</button>
                        </label>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
          )}
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">Contents</div>
          <nav className="flex flex-col gap-0.5 border-l border-border pl-2">
            {parsed.sections.map((s) => (
              <button key={s.slug} onClick={() => jumpTo(s.slug)} className="text-left text-xs text-muted-foreground hover:text-foreground truncate">{s.title}</button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <div className="cg-doc border border-border bg-card p-6 md:p-8" data-testid="guide-content">
          {!ql && <div dangerouslySetInnerHTML={{ __html: mdToHtml(parsed.introMd) }} />}
          {visible.length === 0 ? <p className="text-sm text-muted-foreground">No sections match your search.</p> :
            visible.map((s) => (
              <section key={s.slug} id={s.slug} ref={(el) => (refs.current[s.slug] = el)} data-testid={`guide-section-${s.slug}`} className="scroll-mt-6">
                <h2 dangerouslySetInnerHTML={{ __html: highlight(escHtml(s.title), ql) }} />
                {(s.updated || guide.last_updated) && s.title.toLowerCase() !== "table of contents" && (
                  <div className="cg-updated" data-testid={`guide-updated-${s.slug}`}>Updated {fmtDate(s.updated || guide.last_updated)}</div>
                )}
                <div dangerouslySetInnerHTML={{ __html: highlight(mdToHtml(s.bodyMd), ql) }} />
              </section>
            ))}
        </div>
      </div>

      <style>{`
        .cg-doc h1{font-size:1.5rem;font-weight:800;margin:0 0 .5rem}
        .cg-doc h2{font-size:1.15rem;font-weight:700;margin:1.3rem 0 .4rem;border-bottom:1px solid var(--border);padding-bottom:.25rem}
        .cg-doc section:first-child h2{margin-top:0}
        .cg-updated{font-size:.7rem;color:#94a3b8;font-style:italic;margin:-.15rem 0 .55rem}
        .cg-doc h3{font-size:1rem;font-weight:700;margin:1rem 0 .25rem}
        .cg-doc p{font-size:.9rem;line-height:1.6;margin:.35rem 0;color:#334155}
        .cg-doc ul{margin:.35rem 0 .6rem 1.1rem;list-style:disc}
        .cg-doc ol{margin:.35rem 0 .6rem 1.25rem;list-style:decimal}
        .cg-doc img.cg-img{max-width:100%;height:auto;display:block;margin:.7rem 0;border:1px solid var(--border);border-radius:3px;box-shadow:0 1px 3px rgba(2,13,35,.08)}
        .cg-doc li{font-size:.9rem;line-height:1.55;margin:.15rem 0;color:#334155}
        .cg-doc blockquote{border-left:3px solid #071B3E;background:#f1f5f9;padding:.5rem .75rem;margin:.6rem 0;font-size:.85rem;color:#475569}
        .cg-doc hr{border:0;border-top:1px solid var(--border);margin:1.25rem 0}
        .cg-doc code{background:#f1f5f9;padding:.05rem .3rem;font-size:.8rem;border-radius:2px}
        .cg-table{width:100%;border-collapse:collapse;margin:.6rem 0;font-size:.85rem}
        .cg-table td{border:1px solid var(--border);padding:.4rem .55rem;vertical-align:top;color:#334155}
        .cg-table tr:first-child td{background:#f8fafc;font-weight:700}
        .cg-doc mark{background:#fde68a;color:inherit;padding:0 1px;border-radius:2px}
        .cg-doc a.cg-link{color:#071B3E;text-decoration:underline;cursor:pointer}
        .cg-doc a.cg-link:hover{color:#0c2a5e}
      `}</style>
    </div>
  );
}
